import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import AHDateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type FeeSchedulesFilterJSON = z.infer<typeof FeeSchedulesFilterSchema.FeeSchedulesFilterJSONZ>;
export type FeeSchedulesFilterParams = z.infer<typeof FeeSchedulesFilterSchema.FeeSchedulesFilterZ>;

export default class FeeSchedulesFilterSchema {
	public static readonly FeeSchedulesFilterJSONZ = z.object({
		query: z.string().nullish(),
		entityId: z.uuid(),
		minCreatedDate: z.iso.datetime({ offset: true }).nullish(),
		maxCreatedDate: z.iso.datetime({ offset: true }).nullish(),
		isArchived: z.boolean().nullish(),
		isActive: z.boolean().nullish(),
		clientTemplateIds: z.array(z.uuid()).nullish(),
		vendorTemplateIds: z.array(z.uuid()).nullish(),
	});

	public static readonly FeeSchedulesFilterZ = z.object({
		query: z.string().optional(),
		entityId: z.instanceof(Guid),
		minCreatedDate: AHDateSchema.type.optional(),
		maxCreatedDate: AHDateSchema.type.optional(),
		isArchived: AHBoolean.type.optional(),
		isActive: AHBoolean.type.optional(),
		clientTemplateIds: z.array(z.instanceof(Guid)).optional(),
		vendorTemplateIds: z.array(z.instanceof(Guid)).optional(),
	});

	public static validate(values: unknown): FeeSchedulesFilterParams {
		return parseOrThrow(FeeSchedulesFilterSchema.FeeSchedulesFilterZ, values, {
			logError: true,
			schemaName: "FeeSchedulesFilterSchema",
			operation: "validate",
			payloadSample: values,
		});
	}

	public static validateJSON(values: unknown): FeeSchedulesFilterJSON {
		return parseOrThrow(FeeSchedulesFilterSchema.FeeSchedulesFilterJSONZ, values, {
			logError: true,
			schemaName: "FeeSchedulesFilterSchema",
			operation: "validateJSON",
			payloadSample: values,
		});
	}

	public static isValid(values: unknown): values is FeeSchedulesFilterParams {
		try {
			FeeSchedulesFilterSchema.validate(values);
			return true;
		} catch {
			return false;
		}
	}

	public static isValidJSON(values: unknown): values is FeeSchedulesFilterJSON {
		try {
			FeeSchedulesFilterSchema.validateJSON(values);
			return true;
		} catch {
			return false;
		}
	}

	public static deserialize(data: FeeSchedulesFilterJSON): FeeSchedulesFilterParams {
		const parsed = parseOrThrow(FeeSchedulesFilterSchema.FeeSchedulesFilterJSONZ, data, {
			logError: true,
			schemaName: "FeeSchedulesFilterSchema",
			operation: "deserialize",
			payloadSample: data,
		});
		return FeeSchedulesFilterSchema.codec.decode(parsed);
	}

	public static serialize(params: FeeSchedulesFilterParams): FeeSchedulesFilterJSON {
		const parsed = FeeSchedulesFilterSchema.validate(params);
		return FeeSchedulesFilterSchema.codec.encode(parsed);
	}

	private static readonly codec = z.codec(
		FeeSchedulesFilterSchema.FeeSchedulesFilterJSONZ,
		FeeSchedulesFilterSchema.FeeSchedulesFilterZ,
		{
			decode: (json) => ({
				query: json.query ?? undefined,
				entityId: Guid.fromJSON(json.entityId)!,
				minCreatedDate: AHDate.fromJSON(json.minCreatedDate) ?? undefined,
				maxCreatedDate: AHDate.fromJSON(json.maxCreatedDate) ?? undefined,
				isArchived: AHBoolean.fromJSON(json.isArchived) ?? undefined,
				isActive: AHBoolean.fromJSON(json.isActive) ?? undefined,
				clientTemplateIds: Guid.fromJSONArray(json.clientTemplateIds) ?? undefined,
				vendorTemplateIds: Guid.fromJSONArray(json.vendorTemplateIds) ?? undefined,
			}),
			encode: (params) => ({
				query: params.query ?? null,
				entityId: params.entityId.toJSON(),
				minCreatedDate: params.minCreatedDate?.toJSON(),
				maxCreatedDate: params.maxCreatedDate?.toJSON(),
				isArchived: params.isArchived?.toJSON() ?? null,
				isActive: params.isActive?.toJSON() ?? null,
				clientTemplateIds: params.clientTemplateIds?.map((id) => id.toJSON()) ?? null,
				vendorTemplateIds: params.vendorTemplateIds?.map((id) => id.toJSON()) ?? null,
			}),
		}
	);
}
